import React from "react";
import { API_BASE } from "../api";

const REPORT_ENDPOINT = API_BASE + "/telemetry/client-error";
const REPORTED = new Set(); // de-dupe window.onerror + Promise reject bursts

/**
 * Best-effort POST of a client-side error to our telemetry ingest. Never
 * throws — if the network / backend is down, we just console.warn. Includes
 * the auth token if we have one so the error is attributed to the logged-in
 * user (makes the Logs timeline filterable by user).
 */
function reportClientError(payload) {
  try {
    // Rough de-dupe: same message+stack line within 10 seconds → skip.
    const sig = `${payload.message || ""}\n${(payload.stack || "").split("\n")[0]}`;
    if (REPORTED.has(sig)) return;
    REPORTED.add(sig);
    setTimeout(() => REPORTED.delete(sig), 10000);

    const token = (() => {
      try { return JSON.parse(localStorage.getItem("videri-forge-user") || "null")?.token || null; }
      catch { return null; }
    })();
    const body = JSON.stringify({
      ...payload,
      url: window.location.href,
      userAgent: navigator.userAgent,
    });
    fetch(REPORT_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body,
      keepalive: true,
    }).catch(() => { /* swallow — don't cascade */ });
  } catch (err) {
    console.warn("[reportClientError] failed:", err?.message);
  }
}

/**
 * Attach global listeners for uncaught errors and unhandled promise
 * rejections. Called once from index.js at app startup so we capture
 * errors that happen outside any React tree (e.g., during imports,
 * Service Worker, or async work).
 *
 * Idempotent — safe to call more than once; we only wire up the first time.
 */
let globalHandlersInstalled = false;
export function installGlobalErrorHandlers() {
  if (globalHandlersInstalled || typeof window === "undefined") return;
  globalHandlersInstalled = true;

  window.addEventListener("error", (event) => {
    // Skip benign resource load failures (broken image, missing chunk) —
    // those show up as events with a non-Error target.
    if (event.error instanceof Error) {
      reportClientError({
        source: "window.onerror",
        message: event.error.message,
        stack: event.error.stack,
        severity: "error",
      });
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    if (reason instanceof Error) {
      reportClientError({
        source: "unhandledrejection",
        message: reason.message,
        stack: reason.stack,
        severity: "error",
      });
    } else if (reason != null) {
      reportClientError({
        source: "unhandledrejection",
        message: String(reason).slice(0, 500),
        severity: "error",
      });
    }
  });
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("[ErrorBoundary]", error, errorInfo);
    // Ship the error to the server so we can diagnose without asking the
    // user to open DevTools. The componentStack is the React-specific
    // "which component threw" — very useful for narrowing the crash.
    reportClientError({
      source: "react-error-boundary",
      message: error?.message || String(error),
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
      severity: "fatal",
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          height: "100vh", background: "#0a0a0f", color: "#e4e4e7", fontFamily: "Inter, sans-serif",
          textAlign: "center", padding: 40,
        }}>
          <h1 style={{ fontSize: 24, marginBottom: 12, color: "#f87171" }}>Something went wrong</h1>
          <p style={{ fontSize: 14, color: "#71717a", marginBottom: 20, maxWidth: 480 }}>
            The app encountered an unexpected error. Please refresh the page to continue.
            <br />
            <span style={{ fontSize: 12, color: "#52525b" }}>The error has been reported to the team automatically.</span>
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: "#8b5cf6", color: "white", border: "none", padding: "10px 24px",
              borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer",
            }}
          >
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
